import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import TabPanel from 'src/components/TabPanel/TabPanel';
import EquipmentFields from './components/EquipmentFields';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepEquipment = ({
  data,
  handleNext,
  handleBack,
  resetRef,
  isEditable,
  isMultiEquipment
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const initialState = useMemo(() => makeInitialState(data), [data]);

  const handleTabChange = useCallback((_, newValue) => {
    setTabValue(newValue);
  }, []);

  const onNext = useCallback(
    values => {
      handleNext(values);
    },
    [handleNext]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        errors,
        handleSubmit,
        handleBlur,
        handleChange,
        touched,
        values,
        resetForm
      }) => {
        resetRef.current = () => resetForm();
        return (
          <form onSubmit={handleSubmit}>
            {isMultiEquipment && values.equipments?.length ? (
              <>
                <AppBar
                  variant="outlined"
                  position="static"
                  className={classes.appBar}
                >
                  <Tabs
                    indicatorColor="primary"
                    textColor="primary"
                    value={tabValue}
                    onChange={handleTabChange}
                    className={classes.appBar}
                  >
                    {values.equipmentsList.map((equipment, index) => (
                      <Tab
                        key={equipment.id}
                        label={`${t('COMMON.EQUIPMENT')} ${index + 1}`}
                        id={`equipment-tab-${index}`}
                        aria-controls={`equipment-tabpanel-${index}`}
                      />
                    ))}
                  </Tabs>
                </AppBar>
                {values.equipmentsList.map((equipment, index) => (
                  <TabPanel key={equipment.id} value={tabValue} index={index}>
                    <Box className={classes.container}>
                      <EquipmentFields
                        basePath={`equipments.${index}`}
                        errors={errors}
                        touched={touched}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        values={values}
                        isEditable={!equipment?.isHidden}
                      />
                    </Box>
                  </TabPanel>
                ))}
              </>
            ) : (
              <Box className={classes.container}>
                <EquipmentFields
                  basePath="equipment"
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  values={values}
                  isEditable={isEditable}
                />
              </Box>
            )}

            <Box
              className={classes.box}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                className={classes.buttons}
                onClick={handleBack}
                color="primary"
                variant="outlined"
              >
                {t('COMMON.BACK')}
              </Button>
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

StepEquipment.propTypes = {
  data: PropTypes.object.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  resetRef: PropTypes.object,
  isEditable: PropTypes.bool,
  isMultiEquipment: PropTypes.bool
};

export default StepEquipment;
